import { format, parseISO, isValid } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { Commission } from '@/types'

/**
 * Calcule le net d'une commission en tenant compte du mode (% ou montant fixe).
 * Source unique de vérité — à utiliser partout (tables, charts, exports, KPIs).
 *
 * Mode 'fixed' : net = fixed_amount (aucun calcul, aucun split partenaire)
 * Mode 'percent' (défaut) : net = tjm × jours × taux × (1 − split_partenaire)
 */
export function calcCommissionNet(c: Commission): number {
  if (c.commission_mode === 'fixed') return c.fixed_amount ?? 0
  if (c.net_commission != null)      return c.net_commission
  const gross = (c.tjm ?? 0) * (c.working_days ?? 0) * ((c.commission_rate ?? 0) / 100)
  return gross * (1 - (c.partner_split ?? 0) / 100)
}

export function calcCommissionGross(c: Commission): number {
  if (c.commission_mode === 'fixed') return c.fixed_amount ?? 0
  if (c.gross_commission != null)    return c.gross_commission
  return (c.tjm ?? 0) * (c.working_days ?? 0) * ((c.commission_rate ?? 0) / 100)
}

export function calcCdiNet(c: Commission): number {
  if (c.type === 'autre' || c.commission_mode === 'fixed') return c.fixed_amount ?? 0
  return (c.placement_fee ?? 0) + (c.pe_fee ?? 0)
}

/**
 * Calcule une commission freelance brute estimée à partir du TJM, du taux et
 * du nombre de jours travaillés — utilisé pour les aperçus/projections avant
 * qu'une Commission n'existe en base (ex. tri des mandats, preview de saisie).
 * Jours par défaut : 20 (valeur canonique alignée sur calcCommissionGross/Net).
 */
export function calcEstimatedCommissionGross(tjm: number, commissionRate: number, workingDays = 20): number {
  return tjm * workingDays * (commissionRate / 100)
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatDate(date: string | Date | undefined, fmt = 'dd/MM/yyyy'): string {
  if (!date) return '—'
  if (typeof date === 'string' && date.toUpperCase() === 'ASAP') return 'ASAP'
  const d = typeof date === 'string' ? parseISO(date) : date
  if (!isValid(d)) return '—'
  return format(d, fmt, { locale: fr })
}

export function formatMonth(month: string): string {
  // month = "2026-05"
  const [year, m] = month.split('-')
  const date = new Date(parseInt(year), parseInt(m) - 1, 1)
  return format(date, 'MMMM yyyy', { locale: fr })
}

export function currentMonth(): string {
  return format(new Date(), 'yyyy-MM')
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export function progressColor(percent: number): string {
  if (percent >= 70) return 'bg-green-500'
  if (percent >= 30) return 'bg-orange-400'
  return 'bg-red-500'
}

export function progressTextColor(percent: number): string {
  if (percent >= 70) return 'text-green-600'
  if (percent >= 30) return 'text-orange-500'
  return 'text-red-500'
}
